<template>
  <!-- The /features hub list: every feature card grouped by the job it does (data/features-hub.ts).
       Each group has one product scene that plays twice when it scrolls into view, then rests on
       its payoff with a Replay button (FeatureMotion / StoryChatStage). On wide screens the scene
       stays in view with native sticky while the cards scroll; no scroll capture. -->
  <div class="fh">
    <div class="fh-legend-bar">
      <div class="fh-shell fh-legend">
        <p class="fh-legend-title">No tag means every plan. Tags show where a feature, or part of it, starts.</p>
        <ul class="fh-legend-list">
          <li v-for="item in hubLegend.filter((item) => item.tier > 1)" :key="item.cue">
            <span class="fh-cue" :class="`fh-cue--t${item.tier}`"><i aria-hidden="true" />{{ item.cue }}</span>
          </li>
        </ul>
        <NuxtLink
          :to="localePath('/pricing')"
          class="fh-legend-link"
          data-conversion-event="pricing_click"
          data-conversion-label="Features hub plan legend"
          data-conversion-location="/features"
        >
          Compare plans
        </NuxtLink>
      </div>
    </div>

    <section
      v-for="(group, groupIndex) in hubGroups"
      :id="group.id"
      :key="group.id"
      class="fh-group"
      :class="{ 'fh-group--alt': groupIndex % 2 === 1 }"
      :aria-labelledby="`${group.id}-title`"
    >
      <div class="fh-shell fh-group-grid">
        <div class="fh-side">
          <h2 :id="`${group.id}-title`">{{ group.title }}</h2>
          <p class="fh-intro">{{ group.intro }}</p>
          <div class="fh-scene">
            <!-- Server-rendered on their final frame; each hydrates only as it nears the screen, so
                 six scenes add nothing to the first load's main-thread work. -->
            <LazyStoryChatStage v-if="group.scene === 'ai-chat'" class="fh-chat" autoplay :hydrate-on-visible="{ rootMargin: '200px' }" />
            <LazyMotionStage v-else-if="featureMotion[group.scene]" :config="featureMotion[group.scene]" :hydrate-on-visible="{ rootMargin: '200px' }" />
          </div>
        </div>

        <!-- Phones: each job leads with its scene, and its cards open on tap, so the page is about a
             dozen screens; the cards stay in the page and show in full from 640px. -->
        <button
          type="button"
          class="fh-toggle"
          :aria-expanded="openGroups.includes(group.id) ? 'true' : 'false'"
          :aria-controls="`${group.id}-cards`"
          @click="toggleGroup(group.id)"
        >
          {{ openGroups.includes(group.id) ? "Hide" : "Show" }} the {{ group.cards.length }} features
          <span aria-hidden="true">{{ openGroups.includes(group.id) ? "↑" : "↓" }}</span>
        </button>
        <ul :id="`${group.id}-cards`" class="fh-cards" :class="{ 'is-open': openGroups.includes(group.id) }">
          <li v-for="card in group.cards" :key="card.name">
            <NuxtLink class="fh-card" :to="localePath(card.link)">
              <span class="fh-card-head">
                <span class="fh-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path :d="icons[card.icon]" /></svg>
                </span>
                <h3>{{ card.name }}</h3>
              </span>
              <p class="fh-copy">{{ card.copy }}</p>
              <!-- Every-plan cues are left out: most cards are on every plan, so only the gates show. -->
              <span v-if="card.cues.some((cue) => cue.tier > 1)" class="fh-cues">
                <span v-for="cue in card.cues.filter((cue) => cue.tier > 1)" :key="cue.cue + (cue.label || '')" class="fh-cue" :class="`fh-cue--t${cue.tier}`">
                  <i aria-hidden="true" />
                  <template v-if="cue.label">{{ cue.label }}: </template>{{ cue.cue }}
                </span>
              </span>
              <span class="fh-open">Open<span aria-hidden="true"> →</span></span>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script>
import { cardCues, hubGroups as groups, hubLegend } from "~/data/features-hub";
import { featureMotion } from "~/data/feature-motion.js";
import { motionIcons } from "~/data/motion-icons.js";

// Material icon paths (24x24) for the cards, on top of the motion scenes' own set.
const icons = {
  ...motionIcons,
  inbox: "M19 3H4.99c-1.11 0-1.98.89-1.98 2L3 19c0 1.1.88 2 1.99 2H19c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 12h-4c0 1.66-1.35 3-3 3s-3-1.34-3-3H4.99V5H19v10z",
  people: "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
  funnel: "M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0 0 18.95 4H5.04c-.83 0-1.3.95-.79 1.61z",
  folder: "M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
  phone: "M17 1.01 7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z",
  qrCode: "M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zm0 10h2v2h-2zm-6-6h2v2h-2zm2 2h2v2h-2zm-2 2h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm0-4h2v2h-2zm2 2h2v2h-2z",
  language: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z",
  call: "M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z",
  palette: "M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10a2.5 2.5 0 0 0 2.5-2.5c0-.61-.23-1.2-.64-1.67a.528.528 0 0 1-.13-.33c0-.28.22-.5.5-.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9zm5.5 11c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-3-4c-.83 0-1.5-.67-1.5-1.5S13.67 6 14.5 6s1.5.67 1.5 1.5S15.33 9 14.5 9zM5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S7.33 13 6.5 13 5 12.33 5 11.5zm6-4c0 .83-.67 1.5-1.5 1.5S8 8.33 8 7.5 8.67 6 9.5 6s1.5.67 1.5 1.5z",
  upload: "M5 20h14v-2H5v2zm0-10h4v6h6v-6h4l-7-7-7 7z",
  chart: "M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z",
  inventory: "M20 2H4c-1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-1-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4l16-.02V7z",
  balance: "M4 10h3v7H4zm6.5 0h3v7h-3zM2 19h20v3H2zm15-9h3v7h-3zm-5-9L2 6v2h20V6z",
  lock: "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z",
  api: "M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z",
  support: "M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z",
};

const hubGroups = groups.map((group) => ({
  ...group,
  cards: group.cards.map((card) => ({ ...card, cues: cardCues(card) })),
}));

/* name + link only, for the blog end-of-article "Product modules" band: the lead card of each job. */
export const featureListLinks = groups.map(({ cards: [{ name, link }] }) => ({ name, link }));

export default {
  name: "FeaturesList",
  setup() {
    const { localePath } = useWorkspaceLocale();
    const openGroups = ref([]);
    const toggleGroup = (id) => {
      openGroups.value = openGroups.value.includes(id) ? openGroups.value.filter((open) => open !== id) : [...openGroups.value, id];
    };

    return {
      openGroups,
      toggleGroup,
      featureMotion,
      hubGroups,
      hubLegend,
      icons,
      localePath,
    };
  },
};
</script>

<style scoped>
.fh-shell {
  width: min(100% - 2rem, 72rem);
  margin: 0 auto;
}

/* ─── Plan legend ─────────────────────────────────────────────────────── */
.fh-legend-bar {
  border-bottom: 1px solid rgb(226 232 240);
  background: #fff;
}
.dark .fh-legend-bar {
  border-color: rgb(30 41 59);
  background: rgb(2 6 23);
}
.fh-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  padding: 1.1rem 0;
}
.fh-legend-title {
  color: #334155;
  font-size: 0.85rem;
  font-weight: 800;
}
.dark .fh-legend-title { color: #cbd5e1; }
.fh-legend-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.fh-legend-link {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  margin-left: auto;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.dark .fh-legend-link { color: #67e8f9; }

/* ─── Plan cue: the app's plan palette (PlanCardPalettes, as in FeatureMotion) ─── */
.fh-cue {
  --cue: #3f58bf;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid color-mix(in srgb, var(--cue) 32%, transparent);
  border-radius: 5px;
  background: color-mix(in srgb, var(--cue) 7%, transparent);
  padding: 0.2rem 0.5rem;
  color: var(--cue);
  font-size: 0.75rem;
  font-weight: 750;
  line-height: 1.35;
}
.fh-cue i {
  flex: none;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 2px;
  background: currentColor;
}
.fh-cue--t2 { --cue: #b4336b; }
.fh-cue--t3 { --cue: #ad4e00; }
.fh-cue--t4 { --cue: #946000; }
.fh-cue--t5 { --cue: #7948af; }
.dark .fh-cue { --cue: #a5b4fc; }
.dark .fh-cue--t2 { --cue: #f392bd; }
.dark .fh-cue--t3 { --cue: #f6ac6b; }
.dark .fh-cue--t4 { --cue: #f1c95e; }
.dark .fh-cue--t5 { --cue: #bea5f2; }

/* ─── Groups ──────────────────────────────────────────────────────────── */
.fh-group {
  scroll-margin-top: 6rem;
  background: #f8fafc;
  padding: 4rem 0;
}
.fh-group--alt { background: #fff; }
.dark .fh-group { background: rgb(2 6 23); }
.dark .fh-group--alt { background: rgb(15 23 42 / 0.55); }

.fh-group-grid {
  display: grid;
  gap: 2rem;
}
.fh-side { min-width: 0; }
.fh-side h2 {
  margin-top: 0;
  color: #0f172a;
  font-size: clamp(1.6rem, 1.2rem + 1.4vw, 2.2rem);
  font-weight: 850;
  line-height: 1.15;
}
.dark .fh-side h2 { color: #fff; }
.fh-intro {
  margin-top: 0.85rem;
  max-width: 34rem;
  color: #475569;
  font-size: 1rem;
  line-height: 1.65;
}
.dark .fh-intro { color: #cbd5e1; }
.fh-scene { margin-top: 1.75rem; }

/* ─── Cards ───────────────────────────────────────────────────────────── */
.fh-cards {
  display: grid;
  gap: 0.9rem;
  align-content: start;
}
.fh-card {
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;
  border: 1px solid rgb(226 232 240);
  border-radius: 10px;
  background: #fff;
  padding: 1.05rem 1rem 1rem;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}
.fh-group--alt .fh-card { background: #f8fafc; }
.fh-card:hover,
.fh-card:focus-visible {
  border-color: rgb(14 116 144 / 0.5);
  box-shadow: 0 14px 36px rgb(15 23 42 / 0.08);
  transform: translateY(-2px);
}
.fh-card:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .fh-card:hover,
  .fh-card:focus-visible { transform: none; }
}
.dark .fh-card,
.dark .fh-group--alt .fh-card {
  border-color: rgb(30 41 59);
  background: rgb(15 23 42);
}
.dark .fh-card:hover,
.dark .fh-card:focus-visible { border-color: rgb(103 232 249 / 0.4); }

.fh-icon {
  display: inline-grid;
  flex: none;
  width: 1.9rem;
  height: 1.9rem;
  place-items: center;
  border: 1px solid rgb(226 232 240);
  border-radius: 8px;
  background: #f1f5f9;
  color: #0e7490;
}
.dark .fh-icon {
  border-color: rgb(51 65 85);
  background: rgb(2 6 23);
  color: #67e8f9;
}
.fh-icon svg { width: 1.3rem; height: 1.3rem; fill: currentColor; }
.fh-card-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.fh-card h3 {
  margin-top: 0;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 850;
  line-height: 1.25;
}
.dark .fh-card h3 { color: #fff; }
.fh-copy {
  flex: 1;
  margin-top: 0.7rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.55;
}
.dark .fh-copy { color: #94a3b8; }
.fh-cues {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.9rem;
}
.fh-open {
  margin-top: 0.9rem;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 800;
}
.dark .fh-open { color: #67e8f9; }

/* Phones: each group's cards become a two-column grid of icon, name and plan gate, so a job reads
   as one screen; the descriptions return from 640px, and every card still opens its page. */
.fh-toggle { display: none; }
@media (max-width: 639px) {
  .fh-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid rgb(226 232 240);
    border-radius: 8px;
    background: #fff;
    padding: 0.8rem 1rem;
    color: #0e7490;
    font-size: 0.92rem;
    font-weight: 800;
  }
  .dark .fh-toggle { border-color: rgb(30 41 59); background: rgb(15 23 42); color: #67e8f9; }
  .fh-toggle:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
  .fh-group-grid { gap: 1.25rem; }
  .fh-cards:not(.is-open) { display: none; }
  .fh-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
  .fh-card { padding: 0.75rem 0.8rem; }
  .fh-icon { width: 1.9rem; height: 1.9rem; }
  .fh-icon svg { width: 1.1rem; height: 1.1rem; }
  .fh-card h3 { font-size: 0.92rem; }
  .fh-card-head { flex-direction: column; align-items: flex-start; gap: 0.55rem; }
  .fh-copy, .fh-open { display: none; }
  .fh-cues { margin-top: 0.5rem; }
}

@media (min-width: 640px) {
  .fh-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 1024px) {
  .fh-group { padding: 5.5rem 0; }
  .fh-group-grid {
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    gap: 3.5rem;
  }
}

/* The scene stays in view while the cards scroll, on windows tall enough to show all of it
   under the fixed header; shorter windows scroll it with the page. */
@media (min-width: 1024px) and (min-height: 760px) {
  .fh-scene {
    position: sticky;
    top: 6.5rem;
  }
}
</style>
